import type { Character } from '../domain/character.ts';
import type {
  CharacterChangeRequest,
  ChangeNaming,
  ChangeSource,
} from '../domain/characterChange.ts';
import { describeChange } from '../domain/characterChange.ts';
import type { Skill } from '../content/types.ts';
import type { InventoryCatalogue } from '../rules/inventory.ts';
import type { ApplyChangesResult, ChangeProblem } from '../rules/characterChanges.ts';
import { applyChanges, undoChanges } from '../rules/characterChanges.ts';
import type { CharacterRepository } from '../persistence/characterRepository.ts';

/**
 * The application-level door onto character modification.
 *
 * Everything that edits a crawler comes through here: creation, advancement,
 * the Tutorial award, play, and the manual editor. Callers describe WHAT they
 * want changed; the rules layer decides whether it is legal.
 *
 * Preview and commit are separate on purpose. An editing screen shows the
 * player what a batch would do, and only writes it when they accept.
 */

/**
 * What a caller can tell the modification layer about the world it is editing
 * in. Both fields are optional so the existing callers are unaffected.
 *
 *  - `lookupSkill` turns on RULE DCC-EDIT-002 (see rules/characterChanges.ts).
 *    Supply it whenever Skill IDs came from a person rather than the catalogue.
 *  - `naming` only affects the wording of the review lines, never legality.
 */
export interface ModificationContext {
  lookupSkill?: (skillId: string) => Skill | undefined;
  /**
   * Optional Item catalogue lookup.
   *
   * Turns on catalogue-aware inventory checking (RULE DCC-INV-012): an unknown
   * Item ID is refused, and a batch that would leave an impossible Gear Slot
   * layout is refused whole. Supply it whenever a change could touch inventory.
   *
   * Exposed here so an equip service can go through the ORDINARY
   * preview/commit/undo layer. A missing option is not a reason to reach past
   * this wrapper into the mutation pathway underneath it.
   */
  catalogue?: InventoryCatalogue;
  naming?: ChangeNaming;
}

export interface ModificationPreview {
  ok: boolean;
  /** The crawler as it WOULD be. Never saved by previewing. */
  character: Character;
  /** One readable line per change, for a review list. */
  lines: string[];
  problems: ChangeProblem[];
}

export function previewChanges(
  character: Character,
  requests: readonly CharacterChangeRequest[],
  now: string,
  context: ModificationContext = {},
): ModificationPreview {
  const result = applyChanges(character, requests, {
    now,
    lookupSkill: context.lookupSkill,
    catalogue: context.catalogue,
  });
  if (!result.ok) {
    return { ok: false, character, lines: [], problems: result.problems };
  }
  return {
    ok: true,
    character: result.character,
    lines: result.applied.map(
      (entry) => `${describeChange(entry.change, context.naming)} (${entry.reason})`,
    ),
    problems: [],
  };
}

export type CommitOutcome =
  | { ok: true; character: Character; lines: string[]; appliedCount: number }
  | { ok: false; problems: ChangeProblem[] };

/** Applies a batch and saves it. Nothing is written when the batch is refused. */
export async function commitChanges(
  character: Character,
  requests: readonly CharacterChangeRequest[],
  deps: { characters: CharacterRepository; now: () => string },
  context: ModificationContext = {},
): Promise<CommitOutcome> {
  const result = applyChanges(character, requests, {
    now: deps.now(),
    lookupSkill: context.lookupSkill,
    catalogue: context.catalogue,
  });
  if (!result.ok) return { ok: false, problems: result.problems };

  const saved = await deps.characters.save(result.character);
  return {
    ok: true,
    character: saved,
    lines: result.applied.map((entry) => describeChange(entry.change, context.naming)),
    // The caller needs this to offer "undo what I just did": the change log has
    // no notion of a batch, so the size of the batch is the only handle on it.
    appliedCount: result.applied.length,
  };
}

/**
 * Reverses the last `count` entries in the crawler's change log.
 * Used by the editing screen's undo, and by any caller that needs to back out
 * a batch it just applied.
 *
 * NOTE: the reversal is itself appended to the change log. Calling this twice
 * with the same count therefore redoes the original change. It is a one-step
 * "undo what I just did", not an undo stack.
 */
export function undoLast(
  character: Character,
  count: number,
  now: string,
  context: ModificationContext = {},
): ApplyChangesResult {
  const toUndo = character.changeLog.slice(-count);
  if (toUndo.length === 0) {
    return { ok: false, character, problems: [{ index: -1, message: 'Nothing to undo.' }] };
  }
  return undoChanges(character, toUndo, {
    now,
    lookupSkill: context.lookupSkill,
    catalogue: context.catalogue,
  });
}

/**
 * Reverses the last `count` entries and saves the result.
 *
 * Exists so an editing screen never has to save a character it assembled
 * itself: it asks for the undo and this decides whether anything is written.
 */
export async function undoLastAndCommit(
  character: Character,
  count: number,
  deps: { characters: CharacterRepository; now: () => string },
  context: ModificationContext = {},
): Promise<CommitOutcome> {
  const result = undoLast(character, count, deps.now(), context);
  if (!result.ok) return { ok: false, problems: result.problems };

  const saved = await deps.characters.save(result.character);
  return {
    ok: true,
    character: saved,
    lines: result.applied.map((entry) => describeChange(entry.change, context.naming)),
    appliedCount: result.applied.length,
  };
}

/** Convenience for callers building a batch from one source. */
export function batch(
  source: ChangeSource,
  reason: string,
  changes: readonly CharacterChangeRequest['change'][],
): CharacterChangeRequest[] {
  return changes.map((change) => ({ change, source, reason }));
}
